import { useState, ChangeEvent } from 'react';
import {
	Spacer,
	Grid,
	Select,
	NumberField,
} from '@components';
import {
	DEFAULT_RATE_CARD_RANGES, ILane, TRateCardItemDefinition, TRateCardRules, TSettingsRateCardType
} from '@types';
import {
	RateCardSettingsTypes,
	RateCardFormErrors,
	RateCardTypes,
	RateCardActiveItemId
} from '@state';
import { useRecoilState, useRecoilValue } from 'recoil';
import styles from './Rules.module.scss';
import { useClearRCSettingsError, useIsInRoute, useRateCardState, useRateCardRulesState } from '@hooks';
import { getRateCardIndex } from '@utils';

type TProps = {
	isSettings?: boolean;
	type: keyof typeof DEFAULT_RATE_CARD_RANGES;
	isDisabled?: boolean;
}

export const CPKSettings = (props: TProps) => {
	const {
		isSettings = false,
		type,
		isDisabled = false,
	} = props;
	const [rateCardSettingsTypes, setRateCardSettingsTypes] = useRecoilState(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const globalInRoute = useIsInRoute('global');
	const rcActiveItemID = useRecoilValue(RateCardActiveItemId);
	const [rateCard, setRateCard] = useRateCardState();
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		id: rcActiveItemID ?? undefined,
		isSettings: !!isSettings,
		type
	});
	const activeRateCard = rateCardTypes?.find((rateCard) => {
		if (rcActiveItemID) return rateCard.item_definition?.item_id === rcActiveItemID;
		return rateCard.type === type
	}) ?? null;
	const activeSettingsRateCard = rateCardSettingsTypes?.find((rateCard) => {
		if (rcActiveItemID) return rateCard.item_definition?.item_id === rcActiveItemID;
		return rateCard.type === type
	}) ?? null;
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const [rateCardRules, setRateCardRules] = useRateCardRulesState();
	const rules = isSettings ? rateCardRules.settings : rateCardRules.rateCard;
	const [prices, setPrices] = useState({
		cpk_config_base_price: rules?.cpk_config_base_price ?? '',
		min_charge: rules?.min_charge ?? ''
	});
	const cubicConversionVals = [1000, 333, 250, 200, 190, 167, 150, 1];
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();

	const handleItemDefinitionChange = (name: string, value: string, cpkValue?: number) => {
		let customItemName = '';
		switch (name) {
		case 'cpk_config_pallet_width':
			customItemName = 'width_cm';
			break;
		case 'cpk_config_pallet_length':
			customItemName = 'length_cm';
			break;
		case 'cpk_config_pallet_height':
			customItemName = 'height_cm';
			break;
		default:
			customItemName = name;
			break;
		}
		if (isSettings) {
			const updatedSettingsRateCards = rateCardSettingsTypes?.map((rCard) => {
				if (rcActiveItemID && rCard.item_definition?.item_id !== rcActiveItemID) return rCard
				return {
					...rCard,
					rules: {
						...rules,
						[name]: cpkValue
					},
					item_definition: {
						...rCard.item_definition as TRateCardItemDefinition,
						[name]: value
					}
				};
			});
			setRateCardSettingsTypes(updatedSettingsRateCards as TSettingsRateCardType[]);
			return;
		}
		const updatedRateCards = rateCard?.rate_cards?.map((rCard) => {
			if (rcActiveItemID && rCard.item_definition?.item_id !== rcActiveItemID) return rCard
			return {
				...rCard,
				rules: {
					...rules,
					[name]: cpkValue
				},
				item_definition: {
					...rCard.item_definition,
					[customItemName]: value
				}
			};
		});
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		};

		setRateCard(update as ILane);
		// @ts-ignore
		if (rcFormErrors?.rate_card_prices?.[rateCardTypeIndex]?.item_definition?.[name]) {
			clearRCSettingsError(`rate_card_prices.${rateCardTypeIndex}.item_definition.${name}`)
		}
	}

	const updateRules = (e:ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const isNumber = !isNaN(parseFloat(value));
		let realVal = isNumber ? parseFloat(value) : null;
		if (['cpk_config_base_price', 'min_charge'].includes(name)) {
			setPrices({
				...prices,
				[name]: value
			});
			if (isNumber) realVal = parseFloat(parseFloat(value).toFixed(2));
		}
		// @ts-ignore
		clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.rules.${name}`)
		if (type === 'ITEM' &&
			[
				'cpk_config_pallet_width',
				'cpk_config_pallet_height',
				'cpk_config_pallet_length',
			].includes(name)
		) {
			handleItemDefinitionChange(name, value, ((realVal ?? 0) / 100));
			return
		}

		const update = {
			...rules,
			[name]: realVal
		} as TRateCardRules


		const newRules = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardRules(newRules);
	}

	const updateCubicConversion = (e: string) => {
		const update = {
			...rules,
			cpk_config_cubic_conversion: parseInt(e)
		} as TRateCardRules
		const newRules = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardRules(newRules);
		clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.rules.cpk_config_cubic_conversion`)
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.rules?.[key]?._errors?.[0] || '';
	}

	const hasItemError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.item_definition?.[key]?._errors?.[0] || '';
	}

	return (
		<div className={styles.cpkContainer}>
			<Grid columnTemplate={type === 'CPK' ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)'}>
				{
					(type === 'CPK' ||
					(type === 'ITEM' && (
						activeRateCard?.item_definition?.charge_type === 'CPK' ||
						activeSettingsRateCard?.item_definition?.charge_type === 'CPK'
					))) ?
						<NumberField
							label={'Base Price'}
							name={'cpk_config_base_price'}
							value={`${prices.cpk_config_base_price ?? ''}`}
							onChange={updateRules}
							keepErrorSpace={false}
							placeholder={'0.00'}
							leftModule={<span className={`${styles.prefix} ${!prices.cpk_config_base_price && styles.prefixPlaceholder}`}>$</span>}
							disabled={isDisabled}
						/> : null
				}
				<NumberField
					label={'Min. Charge'}
					name={'min_charge'}
					value={`${prices.min_charge ?? ''}`}
					onChange={updateRules}
					keepErrorSpace={false}
					placeholder={'0.00'}
					leftModule={<span className={`${styles.prefix} ${!prices.min_charge && styles.prefixPlaceholder}`}>$</span>}
					disabled={isDisabled}
					required
				/>
				{
					(type === 'CPK' ||
					(type === 'ITEM' && (
						activeRateCard?.item_definition?.charge_type === 'CPK' ||
						activeSettingsRateCard?.item_definition?.charge_type === 'CPK'
					))) ?
						<Select
							label={'Cubic conv.'}
							name={'cpk_config_cubic_conversion'}
							value={`${rules?.cpk_config_cubic_conversion ?? ''}`}
							onChange={updateCubicConversion}
							placeholder={'Cubic metres'}
							keepErrorSpace={false}
							disabled={isDisabled}
							hasError={!!hasError('cpk_config_cubic_conversion')}
							errorMessage={hasError('cpk_config_cubic_conversion')}
						>
							{
								rules?.cpk_config_cubic_conversion ?
									<Select.Option value={null}>Clear</Select.Option> : <></>
							}
							{cubicConversionVals.map((option, i) => {
								return <Select.Option key={i} value={option}>{option}</Select.Option>
							})}
						</Select> : null
				}
				{
					type === 'ITEM' &&
					isSettings &&
					<NumberField
						value={activeSettingsRateCard?.item_definition?.weight_kg ?? ''}
						onChange={(e) => handleItemDefinitionChange('weight_kg', e.target.value)}
						name={'weight_kg'}
						label={'Weight'}
						required
						keepErrorSpace={false}
						disabled={isDisabled}
						hasError={!!hasItemError('weight_kg')}
						errorMessage={'Enter the weight of the item'}
						rightModule={<span className={`${styles.suffix} ${!activeSettingsRateCard?.item_definition?.weight_kg && styles.suffixPlaceholder}`}>kg</span>}
					/>
				}
			</Grid>
			{
				type !== 'ITEM' &&
				(rules?.cpk_config_cubic_conversion ||
				globalInRoute) ?
					<>
						<Spacer size={20} />
						<Grid columnTemplate='repeat(3, 1fr)'>
							<NumberField
								label={'Length'}
								name={'cpk_config_pallet_length'}
								value={`${rules?.cpk_config_pallet_length ?? ''}`}
								onChange={updateRules}
								keepErrorSpace={false}
								rightModule={<span className={`${styles.suffix} ${!rules?.cpk_config_pallet_length && styles.suffixPlaceholder}`}>m</span>}
								required
								hasError={!!hasError('cpk_config_pallet_length')}
								errorMessage={hasError('cpk_config_pallet_length')}
								disabled={isDisabled}
							/>
							<NumberField
								label={'Width'}
								name={'cpk_config_pallet_width'}
								value={`${rules?.cpk_config_pallet_width ?? ''}`}
								onChange={updateRules}
								keepErrorSpace={false}
								rightModule={<span className={`${styles.suffix} ${!rules?.cpk_config_pallet_width && styles.suffixPlaceholder}`}>m</span>}
								required
								hasError={!!hasError('cpk_config_pallet_width')}
								errorMessage={hasError('cpk_config_pallet_width')}
								disabled={isDisabled}
							/>
							<NumberField
								label={'Height'}
								name={'cpk_config_pallet_height'}
								value={`${rules?.cpk_config_pallet_height ?? ''}`}
								onChange={updateRules}
								keepErrorSpace={false}
								rightModule={<span className={`${styles.suffix} ${!rules?.cpk_config_pallet_height && styles.suffixPlaceholder}`}>m</span>}
								required
								hasError={!!hasError('cpk_config_pallet_height')}
								errorMessage={hasError('cpk_config_pallet_height')}
								disabled={isDisabled}
							/>
						</Grid>
					</>
					: null
			}
		</div>
	);
}
